import React, { useState, useMemo } from 'react';
import Loader from "react-loader-spinner";
import {
    FiPlus, FiX, FiSearch, FiCheck, FiTrash2, FiEdit2, FiExternalLink,
    FiShuffle, FiFilm, FiAlertCircle, FiStar,
} from "react-icons/fi";
import { BASE_URL } from '../constants/config';
import { drawTeams, groupSizes, seedingWarnings } from '../utilities/leagueDraw';
import TeamPicker from './TeamPicker';
import SaveBar from './SaveBar';
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import '../styles/leagueManagement.css';

// Название группы уходит в приложение как есть, поэтому пишем его целиком —
// "Группа А", а не "А", иначе в приложении будет просто буква
const GROUP_NAMES = [
    'Группа А', 'Группа Б', 'Группа В', 'Группа Г', 'Группа Д',
    'Группа Е', 'Группа Ж', 'Группа З', 'Группа И', 'Группа К',
];

const UNASSIGNED = 'unassigned';

const resolveImage = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return BASE_URL.url + image;
};

export default function SeasonRoster({
    leagueType,
    teams,
    onTeamsChange,
    groupsState,
    onGroupsChange,
    allTeams,
    allTeamsLoading,
    loading,
    onOpenTeam,
    rosterDirty,
    onSaveRoster,
    onResetRoster,
    groupsDirty,
    onSaveGroups,
    onResetGroups,
    onOpenCeremony,
    saving,
}) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState(null); // null = форма скрыта
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [groupError, setGroupError] = useState(null);
    const [seededIds, setSeededIds] = useState([]);

    const rosterIds = useMemo(() => new Set(teams.map(t => t.team_id)), [teams]);

    const addTeam = (team) => {
        if (rosterIds.has(team.team_id)) return;
        onTeamsChange([...teams, team]);
    };

    const removeTeam = (team) => {
        onTeamsChange(teams.filter(t => t.team_id !== team.team_id));
        if (groupsState.assignment[team.team_id] !== undefined) {
            const assignment = { ...groupsState.assignment };
            delete assignment[team.team_id];
            onGroupsChange({ ...groupsState, assignment });
        }
    };

    // ---- Группы ----

    const canDraw = groupsState.list.length > 0 && teams.length > 0;

    const nextGroupName = () => {
        const used = new Set(groupsState.list.map(g => g.name));
        return GROUP_NAMES.find(name => !used.has(name)) || '';
    };

    const submitNewGroup = () => {
        const trimmed = (newGroupName || '').trim();
        if (!trimmed) {
            setGroupError('Введите название группы.');
            return;
        }
        if (groupsState.list.some(g => g.name === trimmed)) {
            setGroupError('Группа с таким названием уже есть.');
            return;
        }
        const nextId = groupsState.list.reduce((max, g) => Math.max(max, g.id), 0) + 1;
        onGroupsChange({
            ...groupsState,
            list: [...groupsState.list, {
                id: nextId,
                name: trimmed,
                priority: groupsState.list.length + 1,
            }],
        });
        setNewGroupName(null);
        setGroupError(null);
    };

    const submitRename = () => {
        const trimmed = renameValue.trim();
        if (!trimmed) {
            setGroupError('Название группы не может быть пустым.');
            return;
        }
        if (groupsState.list.some(g => g.name === trimmed && g.id !== renamingId)) {
            setGroupError('Группа с таким названием уже есть.');
            return;
        }
        onGroupsChange({
            ...groupsState,
            list: groupsState.list.map(g => (g.id === renamingId ? { ...g, name: trimmed } : g)),
        });
        setRenamingId(null);
        setGroupError(null);
    };

    const deleteGroup = (group) => {
        const assignment = { ...groupsState.assignment };
        Object.keys(assignment).forEach(teamId => {
            if (assignment[teamId] === group.id) delete assignment[teamId];
        });
        onGroupsChange({
            list: groupsState.list.filter(g => g.id !== group.id),
            assignment,
        });
    };

    const assignTeam = (teamId, groupId) => {
        const assignment = { ...groupsState.assignment };
        if (groupId === UNASSIGNED) delete assignment[teamId];
        else assignment[teamId] = Number(groupId);
        onGroupsChange({ ...groupsState, assignment });
    };

    // Сеяные нигде не хранятся и на сервер не уходят: их отмечают
    // прямо перед распределением, дальше отметки не нужны
    const seededSet = new Set(seededIds.map(Number));

    const toggleSeeded = (teamId) => {
        setSeededIds(prev => (prev.some(id => Number(id) === Number(teamId))
            ? prev.filter(id => Number(id) !== Number(teamId))
            : [...prev, Number(teamId)]));
    };

    const warnings = seedingWarnings(
        teams.length, groupsState.list.length, seededIds.length
    );

    const randomDistribute = () => {
        const { assignment } = drawTeams(teams, groupsState.list, seededIds);
        onGroupsChange({ ...groupsState, assignment });
    };

    const buckets = useMemo(() => {
        if (leagueType !== 2) return null;
        const sorted = [...groupsState.list].sort((a, b) => a.priority - b.priority);
        return {
            groups: sorted.map(group => ({
                group,
                teams: teams.filter(t => groupsState.assignment[t.team_id] === group.id),
            })),
            unassigned: teams.filter(t => groupsState.assignment[t.team_id] === undefined),
        };
    }, [leagueType, teams, groupsState]);

    const teamLogo = (team) => (
        <img
            src={resolveImage(team.image) || defaultTeamLogo}
            alt={team.name}
            onError={(e) => { e.target.src = defaultTeamLogo; }}
        />
    );

    const teamNameButton = (team) => (
        <button
            type="button"
            className="rosterTeamName"
            onClick={() => onOpenTeam(team.team_id)}
            title="Открыть страницу команды"
        >
            {seededSet.has(Number(team.team_id)) && (
                <FiStar className="seedMark" title="Сеяная" />
            )}
            {team.name}
            <FiExternalLink className="rosterTeamLink" />
        </button>
    );

    return (
        <>
            {/* ---- Команды-участники: related_leagues ---- */}
            <div className="settingsCard seasonRosterCard">
                <div className="settingsCardHeader settingsCardHeaderRow">
                    <div>
                        <h2>
                            Команды — участники
                            {!loading && <span className="teamsCount">{teams.length}</span>}
                            {rosterDirty && <span className="seasonBlockDirty">не сохранено</span>}
                        </h2>
                        <p>Кто играет в лиге в этом сезоне.</p>
                    </div>
                    <button className="actionButtonSolid" onClick={() => setPickerOpen(v => !v)}>
                        <FiPlus style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        {pickerOpen ? 'Закрыть' : 'Добавить команду'}
                    </button>
                </div>

                <div className="settingsCardBody">
                    {pickerOpen && (
                        <TeamPicker
                            allTeams={allTeams}
                            loading={allTeamsLoading}
                            isPicked={(teamId) => rosterIds.has(teamId)}
                            onPick={addTeam}
                        />
                    )}

                    {loading ? (
                        <div className="teamsLoader">
                            <Loader type="Oval" color="#667eea" height={36} width={36} />
                        </div>
                    ) : teams.length === 0 ? (
                        <p className="teamsEmpty">
                            В этом сезоне команд пока нет. Добавьте их кнопкой выше.
                        </p>
                    ) : (
                        <div className="rosterList">
                            {teams.map(team => (
                                <div key={team.team_id} className="rosterRow">
                                    {teamLogo(team)}
                                    {teamNameButton(team)}
                                    <button
                                        type="button"
                                        className="rosterRemove"
                                        onClick={() => removeTeam(team)}
                                        title="Убрать из сезона"
                                    >
                                        <FiX />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <SaveBar
                    variant="footer"
                    dirty={rosterDirty}
                    onSave={onSaveRoster}
                    onReset={onResetRoster}
                    label="Сохранить команды"
                    title="Команды"
                    hint="Состав команд изменен и не сохранен"
                    saveDisabled={saving}
                />
            </div>

            {/* ---- Группы: league_groups ---- */}
            {leagueType === 2 && !loading && (
                <>
                    <div className="settingsCard seasonGroupsFull">
                        <div className="settingsCardHeader settingsCardHeaderRow">
                            <div>
                                <h2>
                                    Группы
                                    <span className="teamsCount">{groupsState.list.length}</span>
                                    {groupsDirty && <span className="seasonBlockDirty">не сохранено</span>}
                                </h2>
                                <p>Как участники разбиты по группам. По ним строятся расписание и таблицы.</p>
                            </div>
                            {newGroupName === null && (
                                <div className="groupsToolbarActions">
                                    <button
                                        className="actionButtonGhost"
                                        onClick={() => { setNewGroupName(nextGroupName()); setGroupError(null); }}
                                    >
                                        <FiPlus style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                                        Создать группу
                                    </button>
                                    <button
                                        className="actionButtonGhost"
                                        onClick={randomDistribute}
                                        disabled={!canDraw}
                                        title={canDraw ? '' : 'Нужны хотя бы одна группа и одна команда'}
                                    >
                                        <FiShuffle style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                                        Раскидать случайно
                                    </button>
                                    <button
                                        className="actionButtonSolid"
                                        onClick={() => onOpenCeremony(seededIds)}
                                        disabled={!canDraw}
                                        title={canDraw ? '' : 'Нужны хотя бы одна группа и одна команда'}
                                    >
                                        <FiFilm style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                                        Жеребьёвка
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="settingsCardBody">
                            {newGroupName !== null && (
                                <>
                                    <div className="seasonScopeNote">
                                        <FiAlertCircle style={{ marginRight: '8px', flexShrink: 0 }} />
                                        <span>
                                            Название пишите целиком — <strong>«Группа А»</strong>, а не «А».
                                            В приложении оно выводится как есть, и одна буква будет
                                            выглядеть непонятно.
                                        </span>
                                    </div>
                                    <div className="inlineForm" style={{ marginBottom: '16px' }}>
                                        <input
                                            type="text"
                                            className="modalInput inlineInput"
                                            value={newGroupName}
                                            maxLength={100}
                                            placeholder="например «Группа А»"
                                            autoFocus
                                            onChange={(e) => setNewGroupName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') submitNewGroup();
                                                if (e.key === 'Escape') { setNewGroupName(null); setGroupError(null); }
                                            }}
                                        />
                                        <button className="actionButtonSolid" onClick={submitNewGroup}>
                                            Создать
                                        </button>
                                        <button
                                            className="iconButton"
                                            onClick={() => { setNewGroupName(null); setGroupError(null); }}
                                            title="Отмена"
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                </>
                            )}

                            {groupError && <span className="fieldError">{groupError}</span>}

                            {groupsState.list.length === 0 ? (
                                <div className="seasonScopeNote">
                                    Групп пока нет. Создайте их («Группа А», «Группа Б», …)
                                    и распределите команды.
                                </div>
                            ) : (
                                <>
                                    {canDraw && (
                                        <p className="drawHint">
                                            При случайном распределении получится по{' '}
                                            {groupSizes(teams.length, groupsState.list.length).join(' / ')}{' '}
                                            команд в группах.
                                        </p>
                                    )}

                                    {/* Сеяные: их разводят по разным группам,
                                        чтобы фавориты не встретились на групповом этапе */}
                                    <div className="seedBlock">
                                        <div className="seedBlockHead">
                                            <span className="seedBlockTitle">
                                                <FiStar />
                                                Сеяные команды
                                                <span className="teamsCount">{seededIds.length}</span>
                                            </span>
                                            {seededIds.length > 0 && (
                                                <button
                                                    className="linkButton"
                                                    onClick={() => setSeededIds([])}
                                                >
                                                    снять все
                                                </button>
                                            )}
                                        </div>
                                        <p className="seedBlockHint">
                                            Отметьте команды, которые не должны попасть в одну группу.
                                            При жеребьёвке они разводятся по разным группам, остальные
                                            добираются так, чтобы группы получились равными.
                                        </p>

                                        {warnings.map((warning, index) => (
                                            <div key={index} className="seedWarning">
                                                <FiAlertCircle style={{ marginRight: '8px', flexShrink: 0 }} />
                                                <span>{warning}</span>
                                            </div>
                                        ))}

                                        <div className="seedList">
                                            {teams.map(team => {
                                                const isSeeded = seededSet.has(Number(team.team_id));
                                                return (
                                                    <button
                                                        type="button"
                                                        key={team.team_id}
                                                        className={`seedChip ${isSeeded ? 'seedChipOn' : ''}`}
                                                        onClick={() => toggleSeeded(team.team_id)}
                                                    >
                                                        <FiStar />
                                                        {team.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {buckets.groups.map(({ group, teams: groupTeams }) => (
                                        <div key={group.id} className="groupBlock">
                                            <div className="groupBlockHeader">
                                                {renamingId === group.id ? (
                                                    <div className="inlineForm">
                                                        <input
                                                            type="text"
                                                            className="modalInput inlineInput"
                                                            value={renameValue}
                                                            maxLength={100}
                                                            autoFocus
                                                            onChange={(e) => setRenameValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') submitRename();
                                                                if (e.key === 'Escape') { setRenamingId(null); setGroupError(null); }
                                                            }}
                                                        />
                                                        <button className="actionButtonSolid" onClick={submitRename}>
                                                            Сохранить
                                                        </button>
                                                        <button
                                                            className="iconButton"
                                                            onClick={() => { setRenamingId(null); setGroupError(null); }}
                                                            title="Отмена"
                                                        >
                                                            <FiX />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="groupBlockName">
                                                            {group.name}
                                                            <span className="teamsCount">{groupTeams.length}</span>
                                                        </span>
                                                        <div className="groupBlockActions">
                                                            <button
                                                                className="iconButton"
                                                                onClick={() => {
                                                                    setRenamingId(group.id);
                                                                    setRenameValue(group.name);
                                                                    setGroupError(null);
                                                                }}
                                                                title="Переименовать"
                                                            >
                                                                <FiEdit2 />
                                                            </button>
                                                            <button
                                                                className="iconButton iconButtonDanger"
                                                                onClick={() => deleteGroup(group)}
                                                                title="Удалить группу"
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            {groupTeams.length === 0 ? (
                                                <p className="teamsEmpty">
                                                    В группе нет команд — назначьте их в списке ниже.
                                                </p>
                                            ) : (
                                                <div className="rosterList">
                                                    {groupTeams.map(team => (
                                                        <div key={team.team_id} className="rosterRow">
                                                            {teamLogo(team)}
                                                            {teamNameButton(team)}
                                                            <select
                                                                className="groupSelect"
                                                                value={groupsState.assignment[team.team_id]}
                                                                onChange={(e) => assignTeam(team.team_id, e.target.value)}
                                                            >
                                                                <option value={UNASSIGNED}>Без группы</option>
                                                                {groupsState.list.map(g => (
                                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <div className="groupBlock">
                                        <div className="groupBlockHeader">
                                            <span className="groupBlockName">
                                                Без группы
                                                <span className="teamsCount">{buckets.unassigned.length}</span>
                                            </span>
                                        </div>
                                        {buckets.unassigned.length === 0 ? (
                                            <p className="teamsEmpty">Все команды распределены по группам.</p>
                                        ) : (
                                            <div className="rosterList">
                                                {buckets.unassigned.map(team => (
                                                    <div key={team.team_id} className="rosterRow">
                                                        {teamLogo(team)}
                                                        {teamNameButton(team)}
                                                        <select
                                                            className="groupSelect"
                                                            value={UNASSIGNED}
                                                            onChange={(e) => assignTeam(team.team_id, e.target.value)}
                                                        >
                                                            <option value={UNASSIGNED}>Без группы</option>
                                                            {groupsState.list.map(g => (
                                                                <option key={g.id} value={g.id}>{g.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <SaveBar
                            variant="footer"
                            dirty={groupsDirty}
                            onSave={onSaveGroups}
                            onReset={onResetGroups}
                            label="Сохранить группы"
                            title="Группы этого сезона"
                            hint={rosterDirty
                                ? 'Сначала сохраните состав — иначе в группы попадут команды не из сезона'
                                : 'Группы изменены и не сохранены'}
                            saveDisabled={saving || rosterDirty}
                        />
                    </div>
                </>
            )}
        </>
    );
}
